import { useCallback } from "react";

import { useJitsu } from "@jitsu/react";
import { createSelector } from "@reduxjs/toolkit";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { selectExamBacktrackDisabled } from "@/features/assignment";
import { selectAuthoritySlice } from "@/features/authority";
import {
  QuestionType,
  useUpdateAnswerBlockMutation,
} from "@/generated/graphql";
import { readAnswerBlock, readAnswerBlocks } from "@/graphql/selectors";
import { selectEditorStoreEditor } from "@/stores/editor-store";
import { isSnapshotEmpty, Snapshot } from "@/stores/snapshot";
import { useWorkContext } from "@/stores/work-context";

import {
  goNextPage,
  goPrevPage,
  goToPage,
  PageInfo,
  selectCurrentPage,
  selectIsFirstPage,
} from "../answer-slice";
import { mapAnswerBlocksToPageInfo } from "../utils/answer-blocks";

/** Navigation actions */
interface NavigationData {
  page: PageInfo;
  goToPage: (page: PageInfo) => void;
  navigate: (direction: "next" | "prev") => void;
  backDisabled: boolean;
}

// Maps every answer block on the current page to its snapshot, including blocks
// with no snapshot yet so an untouched question is evaluated for skip status
// when navigating away.
const selectPageSnapshots = createSelector(
  selectAuthoritySlice,
  selectCurrentPage,
  (authority, page) => {
    const snapshots: Record<string, Snapshot | undefined> = {};
    if (page.page === "answer") {
      snapshots[page.answerId] = authority.snapshots[page.answerId];
      if (page.referencesAnswerId) {
        snapshots[page.referencesAnswerId] =
          authority.snapshots[page.referencesAnswerId];
      }
    }
    return snapshots;
  }
);

/**
 * Hook to create navigation state and actions by merging current graphql cache
 * and page data.
 */
export function useAnswerNavigation(): NavigationData {
  const { track } = useJitsu();
  const { workId } = useWorkContext();

  const dispatch = useAppDispatch();
  const page = useAppSelector(selectCurrentPage);
  const isFirstPage = useAppSelector(selectIsFirstPage);
  const examBacktrackDisabled = useAppSelector(selectExamBacktrackDisabled);

  const [mutateAnswerBlock] = useUpdateAnswerBlockMutation();

  const snapshots = useAppSelector(selectPageSnapshots);

  const navigate = useCallback(
    (direction: "next" | "prev") => {
      for (const [answerBlockId, snapshot] of Object.entries(snapshots)) {
        // Update the skip status if needed
        const skip = maybeUpdateSkipStatus(page, answerBlockId, snapshot);

        if (skip !== undefined) {
          mutateAnswerBlock({
            variables: {
              answerBlockId,
              input: {
                skip,
                bookmark: null,
                confirm: null,
              },
            },
          });
          if (page.page === "answer" && skip) {
            const answerBlock = readAnswerBlock(answerBlockId);
            track(JitsuEvent.QUESTION_SKIPPED, {
              question_id: answerBlock?.question?.id,
              question_type: page.questionType,
              answer_block_id: answerBlockId,
            });
          }
        }
      }

      // CASE 1 - Exam and backtracking is disabled
      if (examBacktrackDisabled) {
        const answerBlocks = readAnswerBlocks(workId);
        const answerPages = mapAnswerBlocksToPageInfo(answerBlocks);
        const nextUnconfirmedPage = answerPages.find(
          (pageInfo) => pageInfo.page === "answer" && !pageInfo.confirm
        );
        if (nextUnconfirmedPage) {
          dispatch(goToPage(nextUnconfirmedPage));
        } else {
          // All questions are confirmed
          dispatch(goToPage({ page: "agreement" }));
        }

        return;
      }

      // CASE 2 - Not exam or backtracking is enabled
      dispatch(direction === "next" ? goNextPage() : goPrevPage());
    },
    [
      page,
      dispatch,
      mutateAnswerBlock,
      track,
      examBacktrackDisabled,
      workId,
      snapshots,
    ]
  );

  const goToPageCb = useCallback(
    (page: PageInfo) => {
      dispatch(goToPage(page));
    },
    [dispatch]
  );

  const backDisabled = isFirstPage || examBacktrackDisabled;

  return {
    page,
    goToPage: goToPageCb,
    navigate,
    backDisabled,
  };
}

/**
 * Decide if the `skip` status should be updated for the current answer page and
 * the corresponding AnswerBlock.
 *
 * @param page
 * @param snapshot Snapshot for the current page
 *
 * @returns true if the skip status should be updated, otherwise false.
 */
function maybeUpdateSkipStatus(
  page: PageInfo,
  answerBlockId: string,
  snapshot?: Snapshot
): boolean | undefined {
  if (page.page === "answer" && page.questionType === QuestionType.Extended) {
    const editor = selectEditorStoreEditor(answerBlockId);
    return editor?.isEmpty === true;
  }

  if (page.page === "answer" && page.questionType) {
    const skip = isSnapshotEmpty(page.questionType, snapshot);
    if (skip !== null) {
      return skip;
    }
  }

  return undefined;
}
